import React, { useEffect } from "react";
import { useStore } from "../../store/store";
import noAccess from "../../util/noAccess";
import { cornerConfigOptions } from "../../initialValues";
import { SelectChangeEvent } from "@mui/material";
import taggedCornerConfigImages from "../../taggedImages/taggedCornerConfigImages";
import calculateCornerPanelWidth from "../../util/formulas/calculateCornerPanelWidth";
import fractionToInt from "../../util/fractionToInt";
import singleDirectionOdds from "../../util/singleDirectionOdds";

function findNumberOfPanels(config: string) {
  const panels = config.split("").find((item) => !isNaN(Number(item)));
  const numOfPanels = Number(panels);
  return numOfPanels;
}

function findBifoldImage(option: string) {
  if (!option) return "";

  var match = taggedCornerConfigImages.find((img) => img.config === option);
  return match;
}

function findFrameWidth(
  netWidth: number,
  cornerType: string,
  side: string,
  primaryDoor: string,
  hasCentor?: boolean,
  isPico?: boolean,
) {
  let frameWidth = 0;

  const isPrimaryDoor = primaryDoor === side;

  if (cornerType === "Inside") {
    let addPrimary = 2;
    let addSecondary = 4.375;

    if (hasCentor) {
      const centorNum = isPico ? 4.3125 : 3;
      addPrimary = centorNum + 7.8076;
      addSecondary = centorNum + 5.5576;
    }

    frameWidth = isPrimaryDoor
      ? netWidth + addPrimary
      : netWidth + addSecondary;
  } else {
    let subPrimary = 4.375;
    let subSecondary = 2.125;

    if (hasCentor) {
      const centorNum = isPico ? 4.3125 : 3;
      const deltaP = isPico ? subPrimary : 4.3005;
      const deltaS = isPico ? subSecondary : 2.0625;

      subPrimary = centorNum + deltaP;
      subSecondary = centorNum + deltaS;
    }

    frameWidth = isPrimaryDoor
      ? netWidth - subPrimary
      : netWidth - subSecondary;
  }

  return frameWidth;
}

function isLargePanel(
  config: string,
  frameWidth: number,
  primaryDoor: string,
  material: string,
) {
  if (!frameWidth || !config) return false;

  const cornerSide = config.split("")[1];

  const isPrimaryDoor =
    (primaryDoor === "Left" && cornerSide === "L") ||
    (primaryDoor === "Right" && cornerSide === "R");

  const numOfPanels = findNumberOfPanels(config);
  const panelWidth = calculateCornerPanelWidth({
    stiles: "",
    numberOfPanels: numOfPanels,
    material,
    width: frameWidth,
    isPrimary: isPrimaryDoor,
    exactConfig: "",
  });

  if (panelWidth) {
    if (panelWidth > 48 || panelWidth < 18) return false;
    if (panelWidth >= 42) return `${config} (Wider Panels)`;
  }

  return config;
}

function filterMap(
  options: string[],
  overall: number,
  primaryDoor: string,
  material: string,
) {
  if (!options || !options.length) return;

  let arr: string[] = [];

  options.forEach((itm) => {
    const panel = isLargePanel(itm, overall, primaryDoor, material);

    if (panel) arr.push(panel);
  });

  return arr;
}

function useConfigSelection(
  hideHandlesWithEvenConfigs: (hide: boolean) => void,
) {
  const addToQuote = useStore((state) => state.addToQuote);
  const material = useStore((state) => state.quote.material);
  const cornerType = useStore((state) => state.quote.cornerType);
  const cornerConfigLeft = useStore((state) => state.quote.exactConfig);
  const cornerConfigRight = useStore((state) => state.quote.cornerConfigRight);

  const addScreen = useStore((state) => state.quote.addScreen);
  const screenType = useStore((state) => state.quote.screenType);

  const isLeftOdd = singleDirectionOdds.includes(cornerConfigLeft);
  const isRightOdd = singleDirectionOdds.includes(cornerConfigRight || "");
  const oddEven = (isLeftOdd && !isRightOdd) || (!isLeftOdd && isRightOdd);

  const leftWidth = useStore((state) => state.quote.unitSize);
  const leftFraction = useStore((state) => state.quote.unitSizeFraction);

  const rightWidth = useStore((state) => state.quote.cornerWidthRight);
  const rightFraction = useStore(
    (state) => state.quote.cornerWidthFractionRight,
  );

  const stileCategory = useStore((state) => state.quote.stileCategory);

  const primaryDoor = useStore((state) => state.quote.primaryDoor);
  const handleErrMsgChange = useStore((state) => state.handleError);
  const errors = useStore((state) => state.errors);

  const overallLeft = findFrameWidth(
    fractionToInt(leftWidth, leftFraction),
    cornerType || "",
    "Left",
    primaryDoor || "",
    addScreen && screenType?.includes("Centor") ? true : false,
    stileCategory === "Pico",
  );
  const overallRight = findFrameWidth(
    fractionToInt(rightWidth || "", rightFraction),
    cornerType || "",
    "Right",
    primaryDoor || "",
    addScreen && screenType?.includes("Centor") ? true : false,
    stileCategory === "Pico",
  );

  useEffect(() => {
    addToQuote({ cornerFrameWidthLeft: overallLeft });
  }, [overallLeft]);

  useEffect(() => {
    addToQuote({ cornerFrameWidthRight: overallRight });
  }, [overallRight]);

  const configOptionsLeft = cornerConfigOptions(overallLeft, "L");
  const configOptionsRight = cornerConfigOptions(overallRight, "R");

  const cornerOptionsLeft = filterMap(
    configOptionsLeft?.length ? configOptionsLeft : [],
    overallLeft,
    primaryDoor || "",
    material,
  );

  const cornerOptionsRight = filterMap(
    configOptionsRight?.length ? configOptionsRight : [],
    overallRight,
    primaryDoor || "",
    material,
  );

  useEffect(() => {
    if (!cornerOptionsLeft?.length) return;
    if (
      !cornerOptionsLeft.includes(cornerConfigLeft) &&
      !cornerOptionsLeft.includes(`${cornerConfigLeft} (Wider Panels)`)
    ) {
      addToQuote({ exactConfig: "" });
    }
  }, [overallLeft, primaryDoor, material]);

  useEffect(() => {
    if (!cornerOptionsRight?.length) return;
    if (
      !cornerOptionsRight.includes(cornerConfigRight || "") &&
      !cornerOptionsRight.includes(`${cornerConfigRight} (Wider Panels)`)
    ) {
      addToQuote({ cornerConfigRight: "" });
    }
  }, [overallRight, primaryDoor, material]);

  useEffect(() => {
    if (!cornerType) return;

    if (cornerConfigLeft && cornerConfigRight && oddEven) {
      const primarySide = isLeftOdd ? "Left" : "Right";
      addToQuote({ primaryDoor: primarySide });
    }

    const bothSidesOdd =
      singleDirectionOdds.includes(cornerConfigLeft) &&
      singleDirectionOdds.includes(cornerConfigRight || "")
        ? true
        : false;

    if (cornerType === "Outside" && bothSidesOdd) {
      return handleErrMsgChange(
        "swingWidthErr",
        "Outside Corner Units cannot have two odd numbered sides",
      );
    }

    return handleErrMsgChange("swingWidthErr", "");
  }, [cornerType, cornerConfigLeft, cornerConfigRight]);

  const imageLeft = findBifoldImage(cornerConfigLeft);
  const imageRight = findBifoldImage(cornerConfigRight || "");

  const handleChangeLeft = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    let update = {
      exactConfig: value,
      stilesAndRails: "",
      stileCategory: "",
    };

    const hideHandles =
      noAccess.includes(value) && noAccess.includes(cornerConfigRight || "");

    if (hideHandles) {
      update["withHandle"] = "";
      update["handleStyle"] = "";
      update["handleColor"] = "";
      update["magnetCatch"] = "";
    }
    addToQuote(update);
    hideHandlesWithEvenConfigs(hideHandles);
  };

  const handleChangeRight = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    let update = {
      cornerConfigRight: value,
      stilesAndRails: "",
      stileCategory: "",
    };
    const hideHandles =
      noAccess.includes(value) && noAccess.includes(cornerConfigLeft);

    if (hideHandles) {
      update["withHandle"] = "";
      update["handleStyle"] = "";
      update["handleColor"] = "";
      update["magnetCatch"] = "";
    }
    addToQuote(update);
    hideHandlesWithEvenConfigs(hideHandles);
  };

  const handleChangePrimary = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    addToQuote({ primaryDoor: value });
    handleErrMsgChange("primaryDoorErr", "");
  };

  useEffect(() => {
    if (!imageLeft || !imageRight) return;

    const imgL =
      primaryDoor === "Right" && imageLeft.image2
        ? imageLeft.image2
        : stileCategory === "Pico" &&
            imageLeft.picoImage &&
            primaryDoor === "Left"
          ? imageLeft.picoImage
          : imageLeft.image;

    const imgR =
      primaryDoor === "Left" && imageRight.image2
        ? imageRight.image2
        : stileCategory === "Pico" &&
            imageRight.picoImage &&
            primaryDoor === "Right"
          ? imageRight.picoImage
          : imageRight.image;

    addToQuote({
      exactConfigImg: imgL,
      exactConfigImgRightCorner: imgR,
    });
  }, [imageLeft, imageRight, primaryDoor]);

  return {
    handleChangeLeft,
    handleChangePrimary,
    handleChangeRight,
    imageLeft,
    imageRight,
    cornerOptionsLeft,
    cornerOptionsRight,
    error: errors.swingWidthErr,
    cornerConfigLeft,
    cornerConfigRight,
    primaryDoor,
    oddEven,
    stileCategory,
  };
}

export default useConfigSelection;
